/**
 * QuoteTimelinePanel — Phase 2B Item H.
 *
 * Side-panel (shadcn Sheet) qui affiche la timeline d'un devis :
 *   - Événements associés via `metadata.quote_id` / `source_quote_id` / `clone_quote_id`.
 *   - Types couverts : devis_envoye, devis_signe, devis_refuse, devis_duplicate,
 *     devis_remap_catalog, ... — évolutif côté backend sans changement UI.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Send, CheckCircle2, XCircle, Clock, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

// Map type → (icône, libellé humain)
const TYPE_META = {
  devis_envoye: { icon: Send, label: "Devis envoyé", cls: "text-blue-400" },
  devis_signe: { icon: CheckCircle2, label: "Devis signé", cls: "text-emerald-400" },
  devis_refuse: { icon: XCircle, label: "Devis refusé", cls: "text-red-400" },
  devis_duplicate: { icon: Copy, label: "Devis dupliqué", cls: "text-amber-400" },
  devis_expire: { icon: Clock, label: "Devis expiré", cls: "text-slate-400" },
  devis_remap_catalog: { icon: FileText, label: "Remap catalogue", cls: "text-slate-400" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-BE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function QuoteTimelinePanel({ open, onOpenChange, quote }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!quote?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${quote.id}/timeline`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [quote?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg border-slate-800 bg-slate-950"
        data-testid="quote-timeline-panel"
      >
        <SheetHeader>
          <SheetTitle className="text-slate-100">
            Historique du devis
            {quote?.number && (
              <span className="ml-2 font-mono text-sm text-slate-400">{quote.number}</span>
            )}
          </SheetTitle>
          <SheetDescription>
            Événements associés à ce devis, triés du plus récent au plus ancien.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <div
              className="rounded-md border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500"
              data-testid="quote-timeline-empty"
            >
              Aucun événement pour ce devis.
            </div>
          ) : (
            <ol className="relative space-y-4 border-l border-slate-800 pl-6">
              {items.map((it, idx) => {
                const meta = TYPE_META[it.type] || {
                  icon: FileText,
                  label: it.type,
                  cls: "text-slate-400",
                };
                const Icon = meta.icon;
                return (
                  <li
                    key={it.id || idx}
                    className="relative"
                    data-testid={`quote-timeline-item-${it.type}-${idx}`}
                  >
                    <div className={`absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border border-slate-800 bg-slate-950 ${meta.cls}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className={`text-sm font-medium ${meta.cls}`}>
                          {meta.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          {fmtDateTime(it.created_at)}
                        </span>
                      </div>
                      {it.content && (
                        <p className="mt-1 text-sm text-slate-300">{it.content}</p>
                      )}
                      {it.metadata && Object.keys(it.metadata).length > 0 && (
                        <details className="mt-2 text-xs text-slate-500">
                          <summary className="cursor-pointer hover:text-slate-400">
                            Détails techniques
                          </summary>
                          <pre className="mt-1 overflow-x-auto rounded bg-slate-950/60 p-2 text-[10px] leading-relaxed">
                            {JSON.stringify(it.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
